import React, { useCallback, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, DeleteOutline, LinkOutlined, Send } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useAutoFetch from '../../../hooks/useAutoFetch';
import useTimezone from '../../../hooks/useTimezone';
import { useCopyLink } from '../../../hooks/useCopyLink';
import { formatCnic } from '../../../utils/cnic';
import { admissionInviteService, type AdmissionInvite } from '../../../services/admissions/admissionInviteService';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import EmptyState from '../../common/EmptyState';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import { tableHeadCellSx } from '../tabLayout';

interface Props {
  cycleId: string;
  canManage: boolean;
  onClose: () => void;
  /** A link was cancelled or sent again — the strip's count may change. */
  onChanged: () => void;
}

const PAGE = 10;

/** The cycle's form links still waiting for families: copy, email again, or cancel. */
const FormLinksDialog: React.FC<Props> = ({ cycleId, canManage, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { formatDate } = useTimezone();
  const copyLink = useCopyLink();
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<AdmissionInvite | null>(null);
  const { data, loading, error, retry } = useAutoFetch(`form-links:${cycleId}:${page}`, () => admissionInviteService.list(cycleId, { page: page + 1, limit: PAGE }));

  const act = useCallback(async (invite: AdmissionInvite, run: () => Promise<unknown>, done: string) => {
    setBusyId(invite._id);
    try {
      await run();
      enqueueSnackbar(done, { variant: 'success' });
      retry();
      onChanged();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Something went wrong', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }, [enqueueSnackbar, retry, onChanged]);

  const items = data?.items ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Form links</Typography>
        <Typography variant="body2" color="text.secondary">
          Sent to families to fill in at home. Each one becomes an application when they submit it.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        {error ? (
          <Alert severity="error" sx={{ mx: 3 }}>{error}</Alert>
        ) : !loading && items.length === 0 ? (
          <Box sx={{ px: 3, py: 2 }}>
            <EmptyState dense icon={<LinkOutlined />} title="No form links waiting" description="Every family that got a link has submitted it." />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell sx={{ pl: 3 }}>Applicant</TableCell>
                <TableCell>Sent to</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell align="right" sx={{ pr: 3 }}>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? <TableRowSkeletons columns={5} rows={4} /> : items.map(i => (
                <TableRow key={i._id} hover>
                  <TableCell sx={{ pl: 3 }}>
                    <Typography variant="body2" fontWeight={600}>{i.name || formatCnic(i.cnic)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: i.name ? undefined : 'monospace' }}>
                      {i.name ? formatCnic(i.cnic) : i.fatherCnic ? `Father ${formatCnic(i.fatherCnic)}` : 'On file'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" noWrap>{i.email || '—'}</Typography>
                    {!i.emailedAt && <Typography variant="caption" color="text.secondary">Not emailed</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={i.class ? 'text.primary' : 'text.secondary'}>{i.class?.name ?? 'Family chooses'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(i.emailedAt ?? i.createdAt)}</Typography>
                    {i.createdBy && <Typography variant="caption" color="text.secondary">by {i.createdBy}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2, whiteSpace: 'nowrap' }}>
                    <Tooltip title="Copy link">
                      <IconButton size="small" onClick={() => copyLink(i.link, 'Form link')}><ContentCopy fontSize="small" /></IconButton>
                    </Tooltip>
                    {canManage && data?.emailOn && i.email && (
                      <Tooltip title={`Email it again to ${i.email}`}>
                        <span>
                          <IconButton size="small" disabled={busyId === i._id} onClick={() => act(i, () => admissionInviteService.resend(i._id), `Form link sent to ${i.email}`)}>
                            <Send fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canManage && (
                      <Tooltip title="Cancel the link">
                        <span>
                          <IconButton size="small" disabled={busyId === i._id} onClick={() => setCancelling(i)}><DeleteOutline fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {(data?.pagination.totalItems ?? 0) > PAGE && (
          <TablePagination
            component="div"
            count={data!.pagination.totalItems}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={PAGE}
            rowsPerPageOptions={[PAGE]}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>

      <ConfirmationDialog
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        onConfirm={() => { const i = cancelling!; setCancelling(null); act(i, () => admissionInviteService.cancel(i._id), 'Form link cancelled'); }}
        title="Cancel this form link?"
        message="The family’s link stops working straight away, and they’re told to contact the admissions office. You can send a new one any time."
        confirmText="Cancel link"
        cancelText="Keep it"
      />
    </Dialog>
  );
};

export default FormLinksDialog;
